import { useCallback, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Modal from 'LuxKit/Modal/Modal'
import MaldivesDirectSearchFlightCalendar from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendar'
import MaldivesDirectSearchFlightCalendarMobile from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendarMobile'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import type { DateRange } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/types'
import { useOfferCharterCalendarData } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useOfferCharterCalendarData'
import { fareClassToUrlMap, parseFareClassFromUrl, type FlightsClassTypes, type FareClassWithReward } from 'constants/flight'
import { toBaseFareClass } from 'lib/flights/rewardFareClasses'
import useMaldivesCharterModalFareClasses from 'hooks/Flights/useMaldivesCharterModalFareClasses'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { parseAsBoolean, parseAsString, useQueryState } from 'nuqs'
import CharterBundleCalendarActions from './CharterBundleCalendarActions'
import config from 'constants/config'
import { LUXURY_ESCAPES } from 'constants/brands'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import useCharterFareClassPolicy from 'hooks/Flights/useCharterFareClassPolicy'
import MaldivesCharterBuyPointsBanner from 'components/Flights/CharterBundle/MaldivesCharterBuyPointsBanner'
import useFeatureFlag from 'hooks/Optimizely/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { useIntl } from 'react-intl'
import { mediaQueryUp } from 'components/utils/breakpoint'

const CHARTER_BUNDLE_MODAL_TEST_ID = 'charter-bundle-calendar-modal'

/**
 * The default Modal header wraps `title` in a `<Heading lineClamp={2}>` which
 * applies `display: -webkit-box` and `-webkit-line-clamp: 2`. Putting
 * interactive JSX (FilterChips, SwitchButton) inside that title caused Safari
 * to strictly enforce the clamp, cropping the chips and showing the line-clamp
 * ellipsis. To avoid the issue at the source we keep `title` as a plain string
 * and render the actions via `headerExtension`. This wrapper re-arranges the
 * modal header grid on desktop+ so the extension area sits on the same row as
 * the title (matching the original visual design) instead of below it.
 *
 * We target the modal header via `data-testid` rather than internal LuxKit
 * class names so the override does not depend on styled-components hashes
 * propagating through `Modal` → `ModalBase` → `react-modal`.
 */
const StyledCharterBundleModal = styled(Modal)`
  ${mediaQueryUp.tablet} {
    &&& {
      max-width: min(var(--safe-size), ${rem(1550)});
    }
  }

  ${mediaQueryUp.desktop} {
    [data-testid="${CHARTER_BUNDLE_MODAL_TEST_ID}-header"][data-testid] {
      grid-template-columns: min-content auto 1fr min-content;
      grid-template-areas:
        "prepended-action title header-actions appended-action"
        ". subtitle subtitle .";

      > .extension-area {
        grid-area: header-actions;
        margin-top: 0;
        justify-self: end;
        align-self: center;
      }

      @media (max-width: ${rem(1175)}) {
        grid-template-columns: min-content 1fr min-content;
        grid-template-areas:
          "prepended-action title appended-action"
          "header-actions header-actions header-actions"
          ". subtitle .";

        > .extension-area {
          justify-self: start;
          margin-top: ${rem(8)};
        }
      }
    }
  }
`

interface Props {
  selectedMonth: string
  onMonthChange: (month: string) => void
  fareClass: FlightsClassTypes
  priceMode: 'total' | 'perPerson'
  onDurationChange: (nights: number) => void
  durationOptions: ReadonlyArray<{ value: number, label: string }>
  onFareClassChange?: (fareClass: FareClassWithReward) => void
  offer: App.HotelOffer
  duration: number
  rooms: Array<OfferPageRoom>
  checkInDate?: string
  dateRangesByMonth: Record<string, Array<DateRange>>
  charterBundleRoute?: App.CharterBundleRoute
  isOpen: boolean
  onClose: () => void
  /** Callback when dates are selected */
  onDateSelect: (option: { departDate: string, returnDate: string, checkIn: string, checkOut: string }) => void
}

function CharterBundleCalendarModal(props: Props) {
  const intl = useIntl()
  const {
    offer,
    duration,
    rooms,
    checkInDate,
    selectedMonth,
    onMonthChange,
    fareClass,
    priceMode,
    onDurationChange,
    durationOptions,
    onFareClassChange,
    dateRangesByMonth,
    charterBundleRoute,
    isOpen,
    onClose,
    onDateSelect,
  } = props

  // Fetch charter calendar data inside the modal so it responds to updates
  const flightCalendarData = useOfferCharterCalendarData({
    offer,
    nights: duration,
    rooms,
    fareClass,
    dateRangesByMonth,
    charterBundleRoute,
  })

  const modalBodyRef = useRef<HTMLDivElement | null>(null)
  const isDesktop = useScreenSizeUp('desktop')
  const isRewardSeatsEnabled = useFeatureFlag(OptimizelyFeatureFlags.maldivesCharterRewardSeats)

  // Read luxPlus state from URL (same as parent component)
  // Default to false if brand is not Luxury Escapes
  const [luxPlusEnabled, setLuxPlusEnabled] = useQueryState(
    'luxPlus',
    parseAsBoolean.withDefault(config.BRAND === LUXURY_ESCAPES),
  )

  // Persist the user's confirmed fare-class pick (after they select a date in the carousel) to
  // the URL so reward selections survive page reloads. The dropdown filter above the calendar
  // does NOT call this — it only updates modal-local filter state (see `cabinFilter` below).
  const [, setFareClassUrl] = useQueryState('fareClass', parseAsString)
  const setFareClassWithUrl = useCallback((fc: FareClassWithReward | null) => {
    if (fc) {
      onFareClassChange?.(fc)
      setFareClassUrl(fareClassToUrlMap[fc])
    }
  }, [onFareClassChange, setFareClassUrl])
  // Read the fare class from the URL so the dropdown and calendar reflect the full
  // reward variant (the initial offer page state selector already handles URL → state sync).
  const [fareClassUrlRaw] = useQueryState('fareClass', parseAsString)
  const fareClassFromUrl = fareClassUrlRaw ? parseFareClassFromUrl(fareClassUrlRaw) : undefined

  // URL-backed cabin filter and rewards toggle — matches search page behaviour.
  // Only fall back to fareClassFromUrl when selectedCabin has never been set (not in URL at all).
  // Once the user picks "All classes" the param is explicitly cleared to null.
  const [selectedCabinRaw, setSelectedCabinRaw] = useQueryState('selectedCabin', parseAsString)
  const cabinFilter = selectedCabinRaw as FlightsClassTypes | null
  const handleCabinFilterChange = useCallback((fc: FareClassWithReward | null) => {
    setSelectedCabinRaw(fc ? toBaseFareClass(fc) : null)
  }, [setSelectedCabinRaw])
  const [rewardSeatsOnly, setRewardSeatsOnly] = useQueryState('societeRewards', parseAsBoolean.withDefault(false))

  // Filter durationOptions to only include those with matching packages
  const filteredDurationOptions = useMemo(() => {
    return durationOptions.filter(option => {
      // Check if offer has a package with matching duration
      return offer.packages.some(pkg => pkg.duration === option.value)
    })
  }, [durationOptions, offer.packages])
  const charterFareClassPolicy = useCharterFareClassPolicy(offer, rooms)
  const allowedFareClassesForModal = useMaldivesCharterModalFareClasses(charterFareClassPolicy.allowedFareClasses)

  // If current duration doesn't have a matching package, switch to first available duration
  useEffect(() => {
    const hasMatchingPackage = offer.packages.some(pkg => pkg.duration === duration)
    if (!hasMatchingPackage && filteredDurationOptions.length > 0) {
      const firstAvailableDuration = filteredDurationOptions[0].value
      if (firstAvailableDuration !== duration) {
        onDurationChange(firstAvailableDuration)
      }
    }
  }, [duration, offer.packages, filteredDurationOptions, onDurationChange])

  // Calculate selected option ID based on current checkIn date and duration
  const selectedOptionId = useMemo(() => {
    if (!checkInDate) return undefined
    return `${checkInDate}-${duration}n`
  }, [checkInDate, duration])

  const handleDateSelect = useCallback((option: { departDate: string, returnDate: string, checkIn: string, checkOut: string, fareClass?: FareClassWithReward }) => {
    const selectedCabin = option.fareClass ?? fareClass
    if (selectedCabin) {
      setFareClassWithUrl(selectedCabin)
    }
    onDateSelect(option)
  }, [onDateSelect, fareClass, setFareClassWithUrl])

  const fareClassForSearch = toBaseFareClass(fareClass)
  const calendarProps = {
    onDateSelect: handleDateSelect,
    nights: duration,
    occupancies: rooms,
    luxPlusEnabled,
    itemBundleDataByDate: flightCalendarData.itemBundleDataByDate,
    dateRangesByMonth,
    selectedMonth,
    onMonthChange,
    fareClass: fareClassForSearch,
    selectedFareClass: fareClassFromUrl ?? fareClass,
    cabinFilter,
    priceMode,
    selectedOptionId,
    mode: 'offer' as const,
    offer,
    rewardSeatsOnly,
  }

  const actionsElement = (
    <CharterBundleCalendarActions
      nights={duration}
      onDurationChange={onDurationChange}
      durationOptions={filteredDurationOptions}
      luxPlusEnabled={luxPlusEnabled}
      onLuxPlusToggle={setLuxPlusEnabled}
      fareClass={cabinFilter}
      onFareClassChange={handleCabinFilterChange}
      cabinFilter={cabinFilter}
      onCabinFilterChange={handleCabinFilterChange}
      availableFareClasses={allowedFareClassesForModal}
      rewardSeatsOnly={rewardSeatsOnly}
      onRewardSeatsOnlyChange={setRewardSeatsOnly}
    />
  )

  const selectTripDatesLabel = intl.formatMessage({
    id: '8y9vgMTYZTXakoijFb6D',
    defaultMessage: 'Select your trip dates',
    description: 'Title for the charter bundle calendar modal in the trip planner',
  })

  return (
    <StyledCharterBundleModal
      isOpen={isOpen}
      ref={modalBodyRef}
      title={selectTripDatesLabel}
      headerExtension={isDesktop ? actionsElement : undefined}
      size="max"
      isPrimaryActionHidden
      onClose={onClose}
      data-testid={CHARTER_BUNDLE_MODAL_TEST_ID}
    >
      {isOpen && isRewardSeatsEnabled && <MaldivesCharterBuyPointsBanner />}
      {isOpen && isDesktop && <MaldivesDirectSearchFlightCalendar {...calendarProps} />}
      {isOpen && !isDesktop && <MaldivesDirectSearchFlightCalendarMobile {...calendarProps} headerActions={actionsElement} />}
    </StyledCharterBundleModal>
  )
}

export default withOfferPageAndReduxState((state) => ({
  offer: state.offer! as App.HotelOffer,
  duration: state.duration,
  rooms: state.rooms,
  checkInDate: state.checkInDate,
  fareClass: state.fareClass,
  priceMode: state.priceMode,
  charterBundleRoute: state.charterBundleRoute,
}), CharterBundleCalendarModal)
